/**
 * Blossom — announcement-bar.js
 * <announcement-bar> — carousel rotation (autoplay with pause control, arrows,
 * per-message colors), marquee pause toggle and dismissal.
 *
 * Dismissal is remembered per content hash (data-dismiss-key) in
 * localStorage under "theme:announcement:dismissed", so a bar re-appears
 * as soon as its messages change.
 */
(() => {
  if (!window.Theme || customElements.get('announcement-bar')) return;

  const STORAGE_KEY = 'theme:announcement:dismissed';

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.mode = this.dataset.mode;
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      this.viewport = this.querySelector('[data-announcement-viewport]');
      this.autoplayButton = this.querySelector('[data-announcement-autoplay]');
      this.index = 0;

      this.querySelector('[data-announcement-close]')?.addEventListener('click', () => this.dismiss(), { signal });

      if (this.mode === 'carousel' && this.slides.length > 1) this.setupCarousel(signal);
      if (this.mode === 'marquee') this.setupMarquee(signal);

      if (Theme.designMode) {
        document.addEventListener(
          'shopify:block:select',
          (event) => {
            if (!this.contains(event.target)) return;
            const slide = event.target.closest('[data-announcement-slide]');
            this.pause(true);
            if (slide) this.show(Number(slide.dataset.index));
            this.querySelector('.marquee')?.classList.add('is-paused');
          },
          { signal }
        );
        document.addEventListener(
          'shopify:block:deselect',
          (event) => {
            if (!this.contains(event.target)) return;
            this.querySelector('.marquee')?.classList.remove('is-paused');
            this.playing = Number(this.dataset.autoplay) > 0 && !Theme.prefersReducedMotion();
            this.resume();
          },
          { signal }
        );
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearInterval(this.timer);
    }

    /* ---------------------------------------------------------- Carousel */
    setupCarousel(signal) {
      this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.step(-1), { signal });
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.step(1), { signal });

      this.seconds = Number(this.dataset.autoplay || 0);
      if (this.seconds <= 0) return;

      if (Theme.prefersReducedMotion()) {
        this.autoplayButton?.setAttribute('hidden', '');
        this.setLive(true);
        return;
      }

      this.playing = true;
      this.addEventListener('mouseenter', () => this.pause(), { signal });
      this.addEventListener('mouseleave', () => this.resume(), { signal });
      this.addEventListener('focusin', () => this.pause(), { signal });
      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) this.resume();
        },
        { signal }
      );
      this.autoplayButton?.addEventListener('click', () => this.toggleAutoplay(), { signal });
      this.resume();
    }

    step(direction) {
      // Manual navigation is announced to screen readers.
      this.setLive(true);
      this.show(this.index + direction);
    }

    show(index) {
      const count = this.slides.length;
      if (!count) return;
      this.index = (index + count) % count;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      const current = this.slides[this.index];
      this.setColor('--announcement-bg', current.dataset.bg);
      this.setColor('--announcement-text', current.dataset.text);
    }

    setColor(property, value) {
      if (value) this.style.setProperty(property, value);
      else this.style.removeProperty(property);
    }

    setLive(polite) {
      this.viewport?.setAttribute('aria-live', polite ? 'polite' : 'off');
    }

    toggleAutoplay() {
      this.playing = !this.playing;
      this.updateAutoplayButton();
      if (this.playing) {
        this.setLive(false);
        this.resume();
      } else {
        this.pause(true);
      }
    }

    updateAutoplayButton() {
      if (!this.autoplayButton) return;
      this.autoplayButton.setAttribute('aria-pressed', String(!this.playing));
      this.autoplayButton.setAttribute(
        'aria-label',
        this.playing ? this.autoplayButton.dataset.labelPause : this.autoplayButton.dataset.labelPlay
      );
    }

    pause(hard = false) {
      clearInterval(this.timer);
      this.timer = null;
      if (hard) {
        this.playing = false;
        this.setLive(true);
        this.updateAutoplayButton();
      }
    }

    resume() {
      if (!this.playing || !this.seconds || this.timer) return;
      this.setLive(false);
      this.timer = setInterval(() => this.show(this.index + 1), this.seconds * 1000);
    }

    /* ----------------------------------------------------------- Marquee */
    setupMarquee(signal) {
      const marquee = this.querySelector('[data-announcement-marquee]');
      if (!marquee || !this.autoplayButton) return;
      if (Theme.prefersReducedMotion()) {
        this.autoplayButton.setAttribute('hidden', '');
        return;
      }
      this.autoplayButton.addEventListener(
        'click',
        () => {
          const paused = marquee.classList.toggle('is-paused');
          this.autoplayButton.setAttribute('aria-pressed', String(paused));
          this.autoplayButton.setAttribute(
            'aria-label',
            paused ? this.autoplayButton.dataset.labelPlay : this.autoplayButton.dataset.labelPause
          );
        },
        { signal }
      );
    }

    /* ----------------------------------------------------------- Dismiss */
    dismiss() {
      const key = this.dataset.dismissKey;
      if (key) {
        const dismissed = Theme.storage.get(STORAGE_KEY, []);
        if (!dismissed.includes(key)) Theme.storage.set(STORAGE_KEY, [...dismissed, key].slice(-20));
      }
      this.pause(true);
      this.hidden = true;
      // Move focus somewhere sensible so keyboard users aren't stranded.
      const main = document.getElementById('MainContent');
      const header = document.querySelector('header-component');
      const target = header?.querySelector('a, button') || main;
      target?.focus({ preventScroll: true });
      window.dispatchEvent(new Event('resize'));
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
})();
